import {
  ExperienceDto,
  MetricArrayType,
  VariationDto,
} from "@intelligems/ig-types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { getInitialStartFilterTime } from "../../api/formatters/dashboard";
import { getDefaultCompareGroup, getInitialControl } from "../helpers";
import type { IExperienceDashboardFilters } from "../types/dashboardFilters";
import { initialDashboardFiltersState } from "../types/dashboardFilters";

const slice = createSlice({
  name: "dashboardFilters",
  initialState: initialDashboardFiltersState,
  reducers: {
    /** Update Selected Experiment Id */
    clearDashboardFilters: (dashboardFilters) => {
      Object.assign(dashboardFilters, initialDashboardFiltersState);
    },
    setDashboardFilter: (
      dashboardFilters,
      action: PayloadAction<IExperienceDashboardFilters>
    ) => {
      Object.assign(dashboardFilters, action.payload);
    },
    setDashboardFilterByExperience: (
      dashboardFilters,
      action: PayloadAction<{
        experience: ExperienceDto;
        page: string;
        dashboardData: MetricArrayType | undefined;
        shouldShowProfit: boolean;
        maxZScore: string;
      }>
    ) => {
      const controlGroup = action.payload.experience.variations.find(
        (group) => group.isControl
      );
      if (controlGroup) {
        dashboardFilters.isZScoreEnabled = true;
        dashboardFilters.maxZScore = action.payload.maxZScore;
        dashboardFilters.testOrdersOnly =
          action.payload.experience.experienceAnalysis.analyticsViewType ===
            "none" ||
          action.payload.experience.experienceAnalysis.analyticsViewType ===
            "test_orders_only";
        dashboardFilters.startTime = null;
        dashboardFilters.endTime = null;
        dashboardFilters.experienceId = action.payload.experience.id;
        dashboardFilters.statSigView = "best";
        dashboardFilters.visitorType = "any";
        dashboardFilters.deviceType = "any";
        dashboardFilters.customEvents = [];
        dashboardFilters.countryCodes = [];
        dashboardFilters.urlParam = {
          queryParam: null,
          queryParamFilterType: null,
          queryParamValue: null,
        };
        dashboardFilters.userBehavior = {
          viewedCheckout: null,
          addedToCart: null,
        };
        dashboardFilters.compareGroupA = getInitialControl(
          action.payload.experience.variations,
          action.payload.page
        );
        dashboardFilters.compareGroupB = getDefaultCompareGroup(
          action.payload.experience.variations,
          controlGroup,
          action.payload.page,
          action.payload.dashboardData,
          action.payload.shouldShowProfit
        );
      }
    },
    setDashboardFilterByPersonalization: (
      dashboardFilters,
      action: PayloadAction<{
        experience: ExperienceDto;
        dashboardData: MetricArrayType | undefined;
        maxZScore: string;
      }>
    ) => {
      dashboardFilters.isZScoreEnabled = true;
      dashboardFilters.testOrdersOnly = false;
      dashboardFilters.startTime = null;
      dashboardFilters.endTime = null;
      dashboardFilters.experienceId = action.payload.experience.id;
      dashboardFilters.compareGroupA = undefined;
      dashboardFilters.compareGroupB = undefined;
      dashboardFilters.statSigView = "best";
      dashboardFilters.deviceType = "any";
      dashboardFilters.visitorType = "any";
      dashboardFilters.maxZScore = action.payload.maxZScore;
      dashboardFilters.customEvents = [];
      dashboardFilters.countryCodes = [];
      dashboardFilters.userBehavior = {
        viewedCheckout: null,
        addedToCart: null,
      };
      dashboardFilters.urlParam = {
        queryParam: null,
        queryParamFilterType: null,
        queryParamValue: null,
      };
    },
    setCompareGroups: (
      dashboardFilters,
      action: PayloadAction<{
        experience: ExperienceDto;
        page: string;
        dashboardData: MetricArrayType | undefined;
        shouldShowProfit: boolean;
      }>
    ) => {
      const controlGroup = action.payload.experience.variations.find(
        (group: VariationDto) => group.isControl
      );
      if (controlGroup) {
        dashboardFilters.compareGroupA = getInitialControl(
          action.payload.experience.variations,
          action.payload.page
        );
        dashboardFilters.compareGroupB = getDefaultCompareGroup(
          action.payload.experience.variations,
          controlGroup,
          action.payload.page,
          action.payload.dashboardData,
          action.payload.shouldShowProfit
        );
      }
    },
    setIsTestOrdersOnly: (
      dashboardFilters,
      action: PayloadAction<{ value: boolean }>
    ) => {
      dashboardFilters.testOrdersOnly = action.payload.value;
      return dashboardFilters;
    },
    updateDashboardFilter: (
      dashboardFilters,
      action: PayloadAction<{
        key: keyof IExperienceDashboardFilters;
        value: any;
      }>
    ) => {
      Object.assign(dashboardFilters, {
        ...dashboardFilters,
        [action.payload.key]: action.payload.value,
      });
    },
  },
});

export const {
  clearDashboardFilters,
  updateDashboardFilter,
  setDashboardFilter,
  setDashboardFilterByExperience,
  setCompareGroups,
  setDashboardFilterByPersonalization,
  setIsTestOrdersOnly,
} = slice.actions;

export const dashboardFiltersReducer = slice.reducer;
